'use client';

/**
 * useTotalPositionsValue Hook
 *
 * Calculates the total USD value across all stuck fund positions.
 * Fetches LP reserves for all positions and computes underlying amounts.
 */
import { useMemo } from 'react';
import { useReadContracts } from 'wagmi';
import { formatUnits, type Address } from 'viem';
import type { UnderlyingAsset } from '@/lib/masterchef-cache/types';
import type { TokenPrice } from '@/lib/pricing-service/pricing-service';

// Minimal ABI for LP token queries
const LP_PAIR_ABI = [{
  type: 'function',
  name: 'totalSupply',
  inputs: [],
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view'
}, {
  type: 'function',
  name: 'getReserves',
  inputs: [],
  outputs: [{
    name: '_reserve0',
    type: 'uint112'
  }, {
    name: '_reserve1',
    type: 'uint112'
  }, {
    name: '_blockTimestampLast',
    type: 'uint32'
  }],
  stateMutability: 'view'
}, {
  type: 'function',
  name: 'token0',
  inputs: [],
  outputs: [{
    name: '',
    type: 'address'
  }],
  stateMutability: 'view'
}, {
  type: 'function',
  name: 'token1',
  inputs: [],
  outputs: [{
    name: '',
    type: 'address'
  }],
  stateMutability: 'view'
}] as const;
interface Position {
  lpTokenAddress: Address;
  stakedAmount: bigint;
  underlyingAssets: UnderlyingAsset[];
  contract: {
    chainId: number;
  };
}
export interface UseTotalPositionsValueOptions {
  positions: Position[];
  prices: Map<string, TokenPrice> | undefined;
  chainId: number;
  enabled?: boolean;
}
export interface UseTotalPositionsValueReturn {
  totalValueUsd: number | null;
  isLoading: boolean;
  error: Error | null;
}
export function useTotalPositionsValue({
  positions,
  prices,
  chainId,
  enabled = true
}: UseTotalPositionsValueOptions): UseTotalPositionsValueReturn {
  // Filter to only LP positions (2 underlying assets)
  const lpPositions = positions.filter(p => p.underlyingAssets.length === 2);
  const singleAssetPositions = positions.filter(p_0 => p_0.underlyingAssets.length === 1);

  // Build contracts array for all LP positions
  const contracts = useMemo(() => {
    return lpPositions.flatMap(position => [{
      address: position.lpTokenAddress,
      abi: LP_PAIR_ABI,
      functionName: 'totalSupply' as const,
      chainId
    }, {
      address: position.lpTokenAddress,
      abi: LP_PAIR_ABI,
      functionName: 'getReserves' as const,
      chainId
    }, {
      address: position.lpTokenAddress,
      abi: LP_PAIR_ABI,
      functionName: 'token0' as const,
      chainId
    }, {
      address: position.lpTokenAddress,
      abi: LP_PAIR_ABI,
      functionName: 'token1' as const,
      chainId
    }]);
  }, [lpPositions, chainId]);

  // Fetch all LP data in one multicall
  const {
    data,
    isLoading,
    error
  } = useReadContracts({
    contracts,
    query: {
      enabled: enabled && lpPositions.length > 0
    }
  });

  // Calculate total value
  const totalValueUsd = useMemo(() => {
    if (!prices) return null;
    let total = 0;
    let hasSomePrice = false;

    // Calculate value for single asset positions
    for (const position_0 of singleAssetPositions) {
      const asset = position_0.underlyingAssets[0];
      const price = prices.get(asset.address.toLowerCase());
      if (price?.price_usd) {
        const amount = parseFloat(formatUnits(position_0.stakedAmount, asset.decimals));
        const value = amount * parseFloat(price.price_usd);
        total += value;
        hasSomePrice = true;
      }
    }

    // Calculate value for LP positions
    if (data && lpPositions.length > 0) {
      for (let i = 0; i < lpPositions.length; i++) {
        const position_1 = lpPositions[i];
        const baseIndex = i * 4;
        const totalSupplyResult = data[baseIndex];
        const reservesResult = data[baseIndex + 1];
        const token0Result = data[baseIndex + 2];
        const token1Result = data[baseIndex + 3];

        // Check all calls succeeded
        if (totalSupplyResult?.status !== 'success' || reservesResult?.status !== 'success' || token0Result?.status !== 'success' || token1Result?.status !== 'success') {
          continue;
        }
        const totalSupply = totalSupplyResult.result as bigint;
        const reserves = reservesResult.result as [bigint, bigint, number];
        const token0Address = (token0Result.result as string).toLowerCase();
        const token1Address = (token1Result.result as string).toLowerCase();
        if (totalSupply === 0n) continue;

        // Match assets to token order
        const asset0 = position_1.underlyingAssets.find(a => a.address.toLowerCase() === token0Address);
        const asset1 = position_1.underlyingAssets.find(a_0 => a_0.address.toLowerCase() === token1Address);
        if (!asset0 || !asset1) {
          // Fallback to provided order
          const [firstAsset, secondAsset] = position_1.underlyingAssets;
          const amount0 = position_1.stakedAmount * reserves[0] / totalSupply;
          const amount1 = position_1.stakedAmount * reserves[1] / totalSupply;
          const price0 = prices.get(firstAsset.address.toLowerCase());
          const price1 = prices.get(secondAsset.address.toLowerCase());
          if (price0?.price_usd) {
            const value_0 = parseFloat(formatUnits(amount0, firstAsset.decimals)) * parseFloat(price0.price_usd);
            total += value_0;
            hasSomePrice = true;
          }
          if (price1?.price_usd) {
            const value_1 = parseFloat(formatUnits(amount1, secondAsset.decimals)) * parseFloat(price1.price_usd);
            total += value_1;
            hasSomePrice = true;
          }
        } else {
          // Calculate user's share
          const amount0_0 = position_1.stakedAmount * reserves[0] / totalSupply;
          const amount1_0 = position_1.stakedAmount * reserves[1] / totalSupply;
          const price0_0 = prices.get(asset0.address.toLowerCase());
          const price1_0 = prices.get(asset1.address.toLowerCase());
          if (price0_0?.price_usd) {
            const value_2 = parseFloat(formatUnits(amount0_0, asset0.decimals)) * parseFloat(price0_0.price_usd);
            total += value_2;
            hasSomePrice = true;
          }
          if (price1_0?.price_usd) {
            const value_3 = parseFloat(formatUnits(amount1_0, asset1.decimals)) * parseFloat(price1_0.price_usd);
            total += value_3;
            hasSomePrice = true;
          }
        }
      }
    }
    return hasSomePrice ? total : null;
  }, [data, lpPositions, singleAssetPositions, prices]);
  return {
    totalValueUsd,
    isLoading: lpPositions.length > 0 ? isLoading : false,
    error: error || null
  };
}